import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";

/**
 * У dev-режимі CRA-проксі (localhost:3000 → :5001) не форвардить повноцінну навігацію
 * (Accept: text/html), тож PDF-ендпоінт відкриваємо напряму на бекенд-хост (як з /auth/google в sheet.jsx).
 */
const SERVER_URL =
    process.env.NODE_ENV === "production"
        ? "https://aqueous-atoll-11213.herokuapp.com"
        : "http://localhost:5001";

/**
 * Картка товару на /stocks: фото + назва + product_id + розміри/кольори (живі з CRM) +
 * склад (живий з техкарт). "Друк" створює PDF на Google Drive (якщо його ще нема) і відкриває
 * у новій вкладці сам файл. "Редагувати" дозволяє переозначити тільки назву й фото (і перезаписує PDF на Drive);
 * розміри/кольори/склад — завжди автоматичні.
 */
export default function ProductCardModal({ show, onHide, productId, productName, mode = "view" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(mode === "edit");
    const [busy, setBusy] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const [imageUrlInput, setImageUrlInput] = useState("");

    const wasModalOpen = useRef(false);
    const lastProductIdInModal = useRef(null);

    useEffect(() => {
        if (!show) {
            wasModalOpen.current = false;
            lastProductIdInModal.current = null;
            return;
        }
        const pidChanged =
            lastProductIdInModal.current !== null && lastProductIdInModal.current !== productId;
        lastProductIdInModal.current = productId;
        const justOpened = !wasModalOpen.current || pidChanged;
        wasModalOpen.current = true;
        if (!justOpened) {
            return;
        }

        setEditing(mode === "edit");
        setError(null);
        setData(null);

        if (!productId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const res = await fetch(`/api/stocks/product-card/${productId}`, {
                    credentials: "include",
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(json.error || "Помилка завантаження картки товару");
                }
                if (!cancelled) {
                    setData(json);
                    setNameInput(json.override?.nameOverride || "");
                    setImageUrlInput(json.override?.imageOverrideUrl || "");
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || "Помилка");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [show, productId, mode]);

    const handlePrint = () => {
        if (!productId) return;
        window.open(`${SERVER_URL}/api/stocks/product-card/${productId}/pdf`, "_blank");
    };

    const handleSave = async () => {
        if (!productId) return;
        setBusy(true);
        setError(null);
        try {
            const res = await fetch(`/api/stocks/product-card/${productId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    nameOverride: nameInput,
                    imageOverrideUrl: imageUrlInput,
                }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(json.error || "Помилка збереження");
            }
            setData(json);
            setEditing(false);
        } catch (e) {
            setError(e.message || "Помилка");
        } finally {
            setBusy(false);
        }
    };

    const displayName = data?.name || productName || `Товар #${productId}`;
    const displayImageUrl = data?.imageUrl || "";

    return (
        <Modal show={show} onHide={onHide} scrollable centered size="lg" dialogClassName="product-card-modal__dialog">
            <Modal.Header closeButton>
                <Modal.Title>{editing ? "Редагування картки товару" : "Картка товару"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Завантаження...</span>
                        </Spinner>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger mb-0" role="alert">
                        {error}
                    </div>
                ) : data ? (
                    <div className="d-flex flex-column flex-md-row gap-4">
                        <div className="flex-shrink-0" style={{ width: 220 }}>
                            {(editing ? imageUrlInput : displayImageUrl) ? (
                                <img
                                    src={editing ? imageUrlInput : displayImageUrl}
                                    alt={displayName}
                                    className="img-thumbnail"
                                    style={{ width: 220, height: 300, objectFit: "contain" }}
                                />
                            ) : (
                                <div
                                    className="img-thumbnail d-flex align-items-center justify-content-center text-muted"
                                    style={{ width: 220, height: 300 }}
                                >
                                    Немає фото
                                </div>
                            )}
                            {editing ? (
                                <Form.Group className="mt-2">
                                    <Form.Label className="small text-muted mb-1">URL фото</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder={data.baseImageUrl || "https://…"}
                                        value={imageUrlInput}
                                        onChange={(e) => setImageUrlInput(e.target.value)}
                                        disabled={busy}
                                    />
                                </Form.Group>
                            ) : null}
                        </div>
                        <div className="flex-grow-1">
                            <p className="text-uppercase text-muted small fst-italic mb-1">Lava Studio</p>
                            {editing ? (
                                <Form.Group className="mb-2">
                                    <Form.Label className="small text-muted mb-1">Назва товару</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={data.baseName}
                                        value={nameInput}
                                        onChange={(e) => setNameInput(e.target.value)}
                                        disabled={busy}
                                    />
                                </Form.Group>
                            ) : (
                                <h4 className="mb-1">{displayName}</h4>
                            )}
                            <p className="text-muted mb-3">ID: {data.product_id}</p>

                            <p className="mb-1">
                                <strong>Розміри:</strong> {data.sizes.length ? data.sizes.join(", ") : "—"}
                            </p>
                            <p className="mb-3">
                                <strong>Кольори:</strong> {data.colors.length ? data.colors.join(", ") : "—"}
                            </p>

                            <p className="mb-1">
                                <strong>Склад:</strong>
                            </p>
                            {data.composition.perColor.length || data.composition.common.length ? (
                                <>
                                    {data.composition.perColor.length ? (
                                        <ul className="mb-2 ps-3">
                                            {data.composition.perColor.map((g, i) => (
                                                <li key={i}>
                                                    {g.colorValue ? <strong>{g.colorValue}: </strong> : null}
                                                    {g.lines.join("; ")}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    {data.composition.common.length ? (
                                        <p className="mb-0 small">
                                            <strong>Загальне (усі кольори): </strong>
                                            {data.composition.common.join("; ")}
                                        </p>
                                    ) : null}
                                </>
                            ) : (
                                <p className="text-muted mb-0">Техкарту для цього товару ще не заповнено.</p>
                            )}
                        </div>
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer className="d-flex align-items-center justify-content-between">
                <div>
                    {!editing && data ? (
                        <Button variant="outline-secondary" size="sm" onClick={() => setEditing(true)} disabled={busy}>
                            Редагувати
                        </Button>
                    ) : null}
                </div>
                <div className="d-flex align-items-center gap-2">
                    {busy && <Spinner animation="border" size="sm" role="status" />}
                    {editing ? (
                        <>
                            <Button variant="outline-secondary" size="sm" onClick={() => setEditing(false)} disabled={busy}>
                                Скасувати
                            </Button>
                            <Button variant="primary" onClick={handleSave} disabled={busy || !data}>
                                Зберегти
                            </Button>
                        </>
                    ) : (
                        <Button variant="dark" onClick={handlePrint} disabled={!data}>
                            Друк
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
}
